/**
 * MasterDataTypeOfClaimFormDialog — Create/Edit `type_of_claim`.
 * 1:1 port of Access `frmMdQryTypeOfClaimAdd`. Compact Sales Contract style.
 *
 * Single-column (field order by Top): Type Claim Code → Type Claim → Note.
 * `type_claim_code` là PK text: required, disable khi edit. VBA: code trống →
 * "Please type Type Claim Code!"; code trùng (create) → "Code is existed!"
 * (BE raise → snackbar).
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdTypeOfClaimMutation,
  useUpdateMdTypeOfClaimMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataTypeOfClaim } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataTypeOfClaim | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  type_claim_code: string
  type_claim: string
  note: string
}

const EMPTY: FormState = {
  type_claim_code: '',
  type_claim: '',
  note: '',
}

export default function MasterDataTypeOfClaimFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createTypeOfClaim, { isLoading: creating }] = useCreateMdTypeOfClaimMutation()
  const [updateTypeOfClaim, { isLoading: updating }] = useUpdateMdTypeOfClaimMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        type_claim_code: initialValue.type_claim_code ?? '',
        type_claim: initialValue.type_claim ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.type_claim_code.trim()
  const typeClaimEmpty = !form.type_claim.trim()
  const canSave = !codeEmpty && !typeClaimEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (codeEmpty || typeClaimEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateTypeOfClaim({
          code: initialValue.type_claim_code,
          body: {
            type_claim: form.type_claim.trim(),
            note: form.note.trim() || null,
          },
        }).unwrap()
        snack.success('Type of claim updated')
      } else {
        await createTypeOfClaim({
          type_claim_code: form.type_claim_code.trim(),
          type_claim: form.type_claim.trim(),
          note: form.note.trim() || null,
        }).unwrap()
        snack.success('Type of claim created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Type of Claim' : 'New Type of Claim'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.type_claim_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Type Claim Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.type_claim_code}
              onChange={(e) => set('type_claim_code', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Type Claim Code cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Type Claim"
              size="small"
              fullWidth
              required
              error={showErrors && typeClaimEmpty}
              value={form.type_claim}
              onChange={(e) => set('type_claim', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
